import { NavLink } from 'react-router-dom';
import CrowLogo from '../brand/CrowLogo';
import { useAuth } from '../auth/AuthProvider';
import { navRoleFor, resolveFleetLink } from './navRole';
import { useCurrentFleetId } from './useCurrentFleetId';
import { useSubscription } from '../../hooks/useSubscription';
import {
  LayoutDashboard, Car, Wrench, Users, FileText,
  Settings, CreditCard, Truck, BarChart3,
  ChevronLeft, ChevronRight, Shield, Bot
} from 'lucide-react';

const navItems = {
  car_owner: [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/vehicle-profiles', icon: Car, label: 'My Vehicles' },
    { to: '/ai', icon: Bot, label: 'AI Assistant' },
    { to: '/billing', icon: CreditCard, label: 'Billing' },
    { to: '/profile', icon: Settings, label: 'Settings' },
  ],
  mechanic: [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/jobs', icon: Wrench, label: 'Jobs' },
    { to: '/customers', icon: Users, label: 'Customers' },
    { to: '/invoices', icon: FileText, label: 'Invoices' },
    { to: '/billing', icon: CreditCard, label: 'Billing' },
    { to: '/profile', icon: Settings, label: 'Settings' },
  ],
  // 'fleet:' entries resolve against the fleet in view (navRole.js). There is
  // no fleet-level service log -- service is logged per vehicle -- so the old
  // '/fleet/log-service' entry, which had no route, is gone.
  fleet_manager: [
    { to: 'fleet:', icon: LayoutDashboard, label: 'Dashboard' },
    { to: 'fleet:/vehicles', icon: Truck, label: 'Vehicles' },
    { to: 'fleet:/reports', icon: BarChart3, label: 'Cost Reports' },
    { to: '/billing', icon: CreditCard, label: 'Billing' },
    { to: '/profile', icon: Settings, label: 'Settings' },
  ],
  admin: [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/vehicle-profiles', icon: Car, label: 'Vehicles' },
    { to: '/jobs', icon: Wrench, label: 'Jobs' },
    { to: '/fleet', icon: Truck, label: 'Fleet' },
    { to: '/admin', icon: Shield, label: 'Admin' },
    { to: '/billing', icon: CreditCard, label: 'Billing' },
    { to: '/profile', icon: Settings, label: 'Settings' },
  ],
};

export default function Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const { canAccessAdmin } = useSubscription();
  const userType = navRoleFor(user?.user_type);
  const fleetId = useCurrentFleetId(userType === 'fleet_manager');
  // Before a fleet exists there is nothing for Vehicles or Cost Reports to
  // open; they would all resolve to /fleet and light up together.
  const items = (navItems[userType] || navItems.car_owner)
    .filter(({ to }) => fleetId || !to.startsWith('fleet:/'));

  return (
    <aside
      className={`
        hidden lg:flex flex-col fixed left-0 top-0 h-full z-30
        transition-all duration-200
        ${collapsed ? 'w-16' : 'w-60'}
        bg-surface-card border-r border-border-default
      `}
    >
      {/* Logo */}
      {/* Collapsed, the rail is 64px: mark and 48px toggle are stacked, since
          side by side they overflowed it by 25px. */}
      <div className={`flex border-b border-border-default ${collapsed
        ? 'flex-col items-center justify-center gap-1 py-2'
        : 'items-center h-16 px-4'}`}
      >
        <CrowLogo variant={collapsed ? 'mark' : 'horizontal'} height={collapsed ? 30 : 28} />
        <button
          onClick={onToggle}
          className={`${collapsed ? '' : 'ml-auto'} min-w-touch min-h-touch flex items-center justify-center rounded-control
            hover:bg-surface-sunken text-ink-muted`}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={18} strokeWidth={1.5} /> : <ChevronLeft size={18} strokeWidth={1.5} />}
        </button>
      </div>

      {/* Nav items */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {items.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={label}
            to={resolveFleetLink(to, fleetId)}
            end={to === '/' || to === 'fleet:'}
            className={({ isActive }) => `
              flex items-center gap-3 mx-2 px-3 min-h-touch rounded-control
              transition-colors text-body
              ${isActive
                ? 'bg-accent-tint text-accent font-semibold'
                : 'text-ink-muted hover:bg-surface-sunken hover:text-ink'}
            `}
          >
            <Icon size={20} strokeWidth={1.5} />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}

        {/* Admin link - only for admins */}
        {canAccessAdmin && (
          <NavLink
            to="/admin"
            className={({ isActive }) => `
              flex items-center gap-3 mx-2 px-3 min-h-touch rounded-control
              transition-colors text-body mt-2
              ${isActive
                ? 'bg-accent-tint text-accent font-semibold'
                : 'text-ink-muted hover:bg-surface-sunken hover:text-ink'}
            `}
          >
            <Shield size={20} strokeWidth={1.5} />
            {!collapsed && <span>Admin</span>}
          </NavLink>
        )}
      </nav>
    </aside>
  );
}
